import { useState, useMemo } from "react";
import { meetupsExcel, meetupsTableItems, meetupsTableList, meetupsTableListWidths } from "../../misc/constants";
import { Activity, MeetupsState, MeetupsTableItemType } from "../../types";
import MeetupsTableItem from "./MeetupsTableItem/MeetupsTableItem";
import arrowUp from "../../assets/svg/arrow-gray-up.svg";
import arrowDown from "../../assets/svg/arrow-gray-down.svg";
import { useGlobalMiscState } from "../../stores/misc";
import { useUniversalFind } from "../../hooks/universal";
import { State } from "@hookstate/core";
import { exportToExcel, userHasRole, getDateOnly } from "../../misc/utils";
import { useGlobalUserState } from "../../stores/user";

type MeetupsTablePropsType = {
  display: string;
  isAddFormOpen: boolean;
  localState: State<MeetupsState>
};

const MeetupsTable = (props: MeetupsTablePropsType) => {
  const userState = useGlobalUserState();
  const user = userState.get();
  const uniqUser = user.uniqUser || {}
  const display = props.display
  const miscState = useGlobalMiscState()
  const activitiesTriggerFetch = miscState.get('activitiesTriggerFetch') || 0;
  const allActivites: Activity[] = useUniversalFind('Activity', {}, {
    include: ['category', 'department', 'target_audience']
  }, activitiesTriggerFetch, undefined, true);
  const localState = props.localState;
  const state = localState.get();
  const { selectedDepartmentId, selectedCategoryId, selectedStatus, search, selectedItem, fromDate, toDate } = state;
  
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const onItemSelect = (activity: Activity) => {
    if (selectedItem?.id === activity.id) {
      localState.selectedItem.set(null);
      miscState.setBatch({
        meetupsFormEditItem: {},
        isMeetupsAddFormOpen: false
      })
    } else {
      localState.selectedItem.set(activity)
      miscState.setBatch({
        meetupsFormEditItem: activity,
        isMeetupsAddFormOpen: true
      })
    }
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const activities = useMemo(() => {
    return allActivites.filter((c) => {
      let retValue = true;
      if (selectedCategoryId?.length) {
        retValue = retValue && c.category_id === parseInt(selectedCategoryId)
      }

      if (selectedDepartmentId?.length) {
        retValue = retValue && c.department_id === parseInt(selectedDepartmentId)
      }

      if (fromDate) {
        retValue = retValue && new Date(c.start_date) >= new Date(fromDate)
      }

      if (toDate) {
        retValue = retValue && new Date(c.start_date) <= new Date(toDate)
      }

      if (selectedStatus?.length) {
        retValue = retValue && c.status === parseInt(selectedStatus)
      }

      if (search?.length) {
        retValue = retValue && (c.name.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase()))
      }

      if (display === "decide") {
        retValue = retValue && c.status === 0;
      }

      if (!userHasRole('view_activities_extended')) {
        retValue = retValue && c.created_by === uniqUser.id
      }

      return retValue;
    }).slice().sort((a, b) => {
    if (!sortField) {
      let aDate: number | null = null;
      let bDate: number | null = null;
      
      if (a.start_date) {
        try {
          const dateA = new Date(a.start_date);
          if (!isNaN(dateA.getTime())) {
            aDate = dateA.getTime();
          }
        } catch (e) {
        }
      }
      
      if (b.start_date) {
        try {
          const dateB = new Date(b.start_date);
          if (!isNaN(dateB.getTime())) {
            bDate = dateB.getTime();
          }
        } catch (e) {
        }
      }
      
      if (aDate !== null && bDate !== null) {
        if (aDate !== bDate) {
          return bDate - aDate;
        }
      }
      
      if (aDate !== null && bDate === null) return -1;
      if (aDate === null && bDate !== null) return 1;
      
      return (b.id || 0) - (a.id || 0);
    }

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case "Заголовок":
        aValue = a.name || "";
        bValue = b.name || "";
        return sortOrder === "asc" 
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      
      case "Дата":
        aValue = new Date(a.start_date).getTime();
        bValue = new Date(b.start_date).getTime();
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      
      case "Департамент":
        aValue = a.department?.title || "";
        bValue = b.department?.title || "";
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      
      case "Категория":
        aValue = a.category?.name || "";
        bValue = b.category?.name || "";
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      
      case "Аудитория":
        aValue = a.target_audience?.label || "Все";
        bValue = b.target_audience?.label || "Все";
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      
      case "Локация":
        aValue = a.location || "";
        bValue = b.location || "";
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      
      case "Статус мероприятия":
        aValue = a.status ?? 0;
        bValue = b.status ?? 0;
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      
      default:
        return 0;
    }
  });
  }, [allActivites, selectedCategoryId, selectedDepartmentId, fromDate, toDate, selectedStatus, search, display, uniqUser.id, sortField, sortOrder]);

  const onExport = () => {
    exportToExcel(`activities_${(fromDate && toDate) ? `${getDateOnly(fromDate).replaceAll('.', '_')}-${getDateOnly(toDate).replaceAll('.', '_')}` : 'all'}_report`, activities, meetupsExcel)()
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="hidden lg:block mt-4 w-full overflow-x-auto">
        {activities.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg w-full px-3 sm:px-4 md:px-5">
            <p className="text-sm">Мероприятия не найдены</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto px-3 sm:px-4 md:px-5">
            <table className="w-full border-collapse bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-white">
                  {meetupsTableList.map((category, index) => {
                    const shouldDisplay =
                      !props.isAddFormOpen ||
                      ["", "Заголовок", "Изображение"].includes(category)

                    if (category === "Статус мероприятия" && display === "decide") {
                      return null;
                    }

                    const foundWidth = meetupsTableListWidths[category];

                    return shouldDisplay && (
                      <th
                        key={index}
                        className={`px-4 pt-2 pb-4 border-b text-left text-sm whitespace-nowrap text-gray-400 font-medium ${!["", "Изображение"].includes(category) ? "cursor-pointer" : ""}`}
                        style={foundWidth ? {
                          width: foundWidth
                        } : {}}
                        onClick={() => !["", "Изображение"].includes(category) && handleSort(category)}
                      >
                        <div className="flex gap-[6px] items-center">
                          {category}
                          {!["", "Изображение"].includes(category) && (
                            <div className="flex flex-col gap-1">
                              <img
                                src={arrowUp}
                                alt="arrow-up"
                                className={`cursor-pointer ${sortField === category && sortOrder === "asc" ? "opacity-100" : "opacity-30"}`}
                              />
                              <img
                                src={arrowDown}
                                alt="arrow-down"
                                className={`cursor-pointer ${sortField === category && sortOrder === "desc" ? "opacity-100" : "opacity-30"}`}
                              />
                            </div>
                          )}
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {activities.map((item, index) => (
                  <MeetupsTableItem
                    display={display}
                    selectedItem={selectedItem}
                    onSelect={onItemSelect}
                    key={index}
                    item={item}
                    index={index}
                    onExport={onExport}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="lg:hidden mt-4 space-y-3 pb-2">
        {activities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">Мероприятия не найдены</p>
          </div>
        ) : (
          activities.map((item, index) => (
            <MeetupsTableItem
              display={display}
              selectedItem={selectedItem}
              onSelect={onItemSelect}
              key={index}
              item={item}
              index={index}
              onExport={onExport}
              isMobile={true}
            />
          ))
        )}
      </div>
    </div>
  )
};

export default MeetupsTable;
